import styled from '@emotion/styled';
import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { config } from 'src/config/config';
import { getMobileAppUsed, isUserDataFetched } from 'src/modules/user/selectors/userSelectors';

import { color, font, mediaQueries } from '../styles/variables';
import { Modal } from './Modal';

import appStoreButton from 'src/public/img/btn-appstore.svg';
import googlePlayButton from 'src/public/img/btn-google-play.svg';
import image from 'src/public/img/mobile-app-info-modal.png';

const StyledModal = styled(Modal)({
    '.ant-modal-content': {
        boxShadow: '0 8px 16px 0 rgba(116, 135, 156, 0.5)',
        borderRadius: 15,

        '.ant-modal-body': {
            padding: 0,
        },
    },
});

const Title = styled.h3(
    {
        color: color.pickledBluewood,
        lineHeight: '40px',
        textAlign: 'center',
        [mediaQueries.sm]: {
            fontSize: font.size.largeExtended,
        },
    },
    font.black(font.size.headerMedium),
);

const Description = styled.p(
    {
        color: color.blueBayoux,
        textAlign: 'center',
    },
    font.medium(font.size.large),
);

const ButtonsWrapper = styled.div({
    display: 'flex',
    justifyContent: 'center',
    marginTop: '2em',
});

const StoreButton = styled.img({
    cursor: 'pointer',
    [mediaQueries.sm]: {
        width: 130,
    },
});

const hasMinimalRequiredTimeElapsed = (closeDate: string | null): boolean => {
    // user will see the modal for the first time
    if (!closeDate) {
        return true;
    }

    const closeDateMoment = moment(closeDate);
    const now = moment();

    // the difference between a close modal date and now in days
    const diff = now.diff(closeDateMoment, 'd');

    // show the modal again only if at least 7 days elapsed
    return diff > 7;
};

const LOCALSTORAGE_KEY = 'mobileAppInfoModalCloseDate';

export const MobileAppInformationModal: React.FC = () => {
    const [modalClosed, setModalClosed] = useState(false);

    const { t } = useTranslation();

    const userDataFetched = useSelector(isUserDataFetched);
    const mobileAppAlreadyUsed = useSelector(getMobileAppUsed);

    const handleModalClose = () => {
        setModalClosed(true);

        const now = moment().toISOString();

        localStorage.setItem(LOCALSTORAGE_KEY, now);
    };

    const handleStoreButtonClick = (link: string) => () => {
        handleModalClose();

        window.open(link, '_blank');
    };

    // possibly null
    const closeDate = localStorage.getItem(LOCALSTORAGE_KEY);

    const minimalRequiredTimeElapsed = hasMinimalRequiredTimeElapsed(closeDate);

    const modalVisible =
        !modalClosed && minimalRequiredTimeElapsed && userDataFetched && !mobileAppAlreadyUsed;

    return (
        <StyledModal visible={modalVisible} footer={null} onCancel={handleModalClose} width={630}>
            <img src={image} style={{ width: '100%' }} />
            <div style={{ padding: '30px 60px' }}>
                <Title>{t('shared:mobile_app_info_modal.download_our_app')}</Title>
                <Description>{t('shared:mobile_app_info_modal.mobile_app_perks')}</Description>

                <ButtonsWrapper>
                    <StoreButton
                        src={appStoreButton}
                        style={{ marginRight: 15 }}
                        onClick={handleStoreButtonClick(config.iOSAppStoreURL)}
                    />
                    <StoreButton
                        src={googlePlayButton}
                        onClick={handleStoreButtonClick(config.AndroidGooglePlayStoreURL)}
                    />
                </ButtonsWrapper>
            </div>
        </StyledModal>
    );
};
